import { IIndicator, IInsight, IInsightDialog, MessageTemplating } from '@jumpstart/core';
import CloseIcon from '@mui/icons-material/Close';
import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { Formik } from 'formik';
import { FormikErrors } from 'formik/dist/types';
import { nanoid } from 'nanoid';
import React, { Fragment, useEffect, useState } from 'react';
import * as yup from 'yup';
import ContentPreviewDialog from './ContentPreviewDialog';
import ContentPreviewLoading from './ContentPreviewLoading';
import DialogForm from './DialogForm';
import { cLength, CLIMITS } from './DialogFormConfig';
import { IValidationData, testDog, testProfile, validateDefault, validateDialogNode } from './utils';
import { ValidationPrompters } from './ValidationPrompter';


const dialogFormSchema = yup.object({
  id: yup.string(),
  notes: yup.string(),
  isAction: yup.boolean(),
  body: yup.string()
    .test('len', `Action/Context must be less than ${CLIMITS.body} characters`, val => cLength(CLIMITS.body, val)),
  question: yup.string()
    .test('len', `Question must be less than ${CLIMITS.question} characters`, val => cLength(CLIMITS.question, val)),
  questionShort: yup.string()
    .test(
      'len',
      `Question (short) must be less than ${CLIMITS.questionShort} characters`,
      val => cLength(CLIMITS.questionShort, val)
    ),
  questionInline: yup.boolean(),
  response: yup.string(),
  // @TODO don't validate after every change
  // body: yup
  //   .string()
  //   .when(
  //     'completionCriteria',
  //     (
  //       completionCriteria,
  //       schema
  //     ) => isCompletion(completionCriteria) ? schema.notRequired() : schema.required('Action is required')
  //   ),
  // question: yup
  //   .string()
  //   .when(
  //     'completionCriteria',
  //     (
  //       completionCriteria,
  //       schema
  //     ) => isCompletion(completionCriteria) ? schema.notRequired() : schema.required('Question is required')
  //   ),
  header: yup.string(),
  answered: yup.boolean(),
  // @TODO don't validate after every change
  // response: yup.string()
  //   .when(
  //     'completionCriteria',
  //     (completionCriteria, schema) => {
  //       console.log('VALIDATE');
  //       const _isCompletion = isCompletion(completionCriteria);
  //       if (_isCompletion) {
  //         return schema.required('Response is required for a completion event');
  //       } else {
  //         return schema.notRequired();
  //       }
  //       // return !isCompletion(completionCriteria) ? schema.notRequired().optional().nullable() : schema.required(
  //       //   'Response is required for a completion event');
  //     }
  //   ),
  // .when('answered',
  //   (answered, schema) => {
  //   console.log(schema);
  //   return !answered ? schema.notRequired() : schema.required('Response is required for a completion event')
  // }
  // ),
  reminders: yup.array().of(yup.object({
    day: yup.number().min(0),
    message: yup.string()
  })),
  // @TODO figure a way to bring the error object individually rather than testing the entire array
  highlights: yup.array()
    .of(yup.string())
    .test('len', `Highlights/Instructions must be less than ${CLIMITS.highlights} characters each`, val => {
      if (val) {
        for (const highlight of val) {
          if (!cLength(CLIMITS.highlights, highlight)) {
            return false;
          }
        }
      }
      return true;
    }),
  metaAttributes: yup.object().nullable().notRequired(),
  widgets: yup.array().of(yup.object({
    widget: yup.string(),
    purpose: yup.string(),
    title: yup.string(),
    meta: yup.object({
      type: yup.string(),
      subtype: yup.string(),
      severity: yup.string()
    }),
    requirements: yup.array().of(yup.string())
  })),
  tips: yup.array().of(yup.object({
    message: yup.string(),
    severity: yup.string()
  })).test('len', `Tips must be less than ${CLIMITS.tips} characters each`, val => {
    if (val) {
      for (const tip of val) {
        if (!cLength(CLIMITS.tips, tip.message)) {
          return false;
        }
      }
    }
    return true;
  }),
  actions: yup.array().of(yup.object({
    code: yup.string(),
    meta: yup.mixed()
  })),
  details: yup.string().nullable().notRequired(),
  points: yup.object({}).nullable().notRequired(),
  completionCriteria: yup.object({
    value: yup.number(),
    min: yup.number(),
    max: yup.number()
  }).nullable().notRequired()
});

export interface IDialogEditor {
  event: IInsight;
  indicator: IIndicator;
  dialog: IInsightDialog;
  action: string | null;
  question: string | null;
  answer: string | null;
  path: string;
  loading: boolean;
  nodeProps: any

  onError(err: Error): void;
  onSubmit(path: string, dialog: IInsightDialog): void;
}

export default function DialogEditor(props: IDialogEditor) {
  const { indicator, path, answer, question, action, dialog, event, onSubmit, onError, loading } = {
    ...props,
    action: MessageTemplating.process(props.action ? props.action : 'N/A', testDog, testProfile),
    answer: MessageTemplating.process(props.answer ? props.answer : 'N/A', testDog, testProfile),
    question: MessageTemplating.process(props.question ? props.question : 'N/A', testDog, testProfile)
  };
  const [node, setNode] = props.nodeProps;
  const [validation, setValidation] = useState<IValidationData>(validateDefault());
  const [confirm, setConfirm] = useState(false);

  const handleSave = (values: IInsightDialog, validateForm: (values?: any) => Promise<FormikErrors<IInsightDialog>>) => {
    return validateForm(values)
      .then((errors: FormikErrors<IInsightDialog>) => {
        if (Object.keys(errors).length === 0) {

          onSubmit(path, {...values, id: values.id && (`${values.id}`).length > 3 ? values.id : nanoid(10)});
          return true;
        } else {
          onError(new Error('Invalid form'));
          console.error('Validation errors');
          return false;
        }
        // console.log('Validated');
      })
      .catch((err: Error) => {
        console.error('Failed', err);
        onError(new Error('Invalid form'));
        return false;
      });
  };

  useEffect(() => {
    if (dialog) {
      setValidation(validateDialogNode(dialog, answer === 'N/A' ? answer : null));
    } else {
      console.error('No validation...');
      setValidation(validateDefault());
    }
  }, [dialog]);

  return (
    <Formik
      initialValues={{
        ...dialog,
        answered: answer !== 'N/A',
        isAction: typeof dialog.isAction === 'boolean' ? dialog.isAction as any : true
      } as any}
      validationSchema={dialogFormSchema}
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={(values, { validateForm }) => {
        console.log('Submitting...');
        handleSave(values, validateForm)
          .then((success: boolean) => {
            if (success) {
              setNode(null)
            }
          })
      }}
    >
      {
        (formikProps) => (
          <Fragment>
            <Dialog
              open={confirm}
              onClose={() => {
                setConfirm(false);
                setNode(null);
              }}
            >
              <DialogContent>
                <DialogContentText>Are you sure you want to exit without saving?</DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setConfirm(false)} autoFocus color="primary">No</Button>
                <Button onClick={() => {
                  setConfirm(false);
                  setNode(null);
                }} color="primary">Yes</Button>
              </DialogActions>
            </Dialog>
            <Dialog
              open={!!node}
              fullScreen
              onClose={() => setNode(null)}
            >
              <DialogActions
                style={{ width: '100%', position: 'absolute' }}
              >
                <IconButton onClick={() => {
                  if (formikProps.dirty) {
                    setConfirm(true);
                  } else {
                    setNode(null);
                  }
                }}>
                  <CloseIcon/>
                </IconButton>
              </DialogActions>
              <DialogTitle>Edit Dialog</DialogTitle>
              <DialogContent>
                <Grid container justifyContent="center">
                  <Grid container direction="column" style={{ padding: 24 }}>

                    <Grid container direction="row" justifyContent="space-between" wrap="nowrap">
                      <Grid item xs={5}>
                        <Paper style={{ height: '100%', padding: 24 }}>
                          <Grid item container direction="column" style={{ maxWidth: 550 }}>
                            <ValidationPrompters validation={validation}/>
                          </Grid>

                          {question && <Grid item direction="column" style={{
                            padding: '14px 18px',
                            backgroundColor: '#FAFAFA',
                            borderRadius: 8,
                            marginBottom: 24,
                            maxWidth: 550,
                            width: '100%'
                          }}>
                            <Typography variant="body2" style={{ marginBottom: 8 }}>Previous Node:</Typography>
                            <ul style={{ paddingRight: 24, paddingLeft: 24, marginBottom: 8 }}>
                              <li style={{ whiteSpace: 'pre-line' }}>Action: {action ? action : 'N/A'}</li>
                              <li style={{ whiteSpace: 'pre-line' }}>Question: {question ? question : 'N/A'}</li>
                            </ul>
                            <Typography variant="body2"><b>Answer</b>: {answer ? answer : 'N/A'}</Typography>
                          </Grid>}


                          <DialogForm
                            {...formikProps}
                            answer={answer}
                            validation={{ ...validation.errors, ...validation.warnings }}
                          />
                        </Paper>
                      </Grid>
                      <Grid item xs={2}>
                        <Grid
                          container
                          direction="column"
                          alignItems="center"
                          justifyContent="space-around"
                          style={{ height: '100%' }}
                        >
                          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) =>
                            <Grid item key={`reload-${i}`}>
                              <Tooltip title="Save and reload changes">
                                <IconButton
                                  size="medium"
                                  onClick={() => handleSave(formikProps.values, formikProps.validateForm)}
                                  style={{backgroundColor: '#2E2E2E'}}
                                >
                                  {loading ? <CircularProgress style={{color: '#FFF'}} size={24}/> : <DoubleArrowIcon fontSize="large" style={{color: '#FFF'}}/> }
                                </IconButton>
                              </Tooltip>
                            </Grid>
                          )}
                        </Grid>
                      </Grid>
                      <Grid id="refContainer" item xs={5}>
                        <Paper style={{ height: '100%' }}>
                          {loading ? <ContentPreviewLoading/> : <ContentPreviewDialog
                            event={event}
                            indicator={indicator}
                            dialog={dialog}
                            title={`${event.name} >> ${indicator.name}`}
                          />}
                        </Paper>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </DialogContent>
            </Dialog>
          </Fragment>
        )}
    </Formik>
  );
}
